import { createFileRoute } from '@tanstack/react-router'
import {
  Zap,
  Clock,
  Keyboard,
  FileText,
  Bell,
  Lightbulb,
  Loader2,
  Info,
  RotateCcw,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/toast'
import {
  useEfficiencyPreferences,
  useUpdateEfficiencyPreferences,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/settings/efficiency' as never)({
  component: EfficiencySettingsPage,
})

/**
 * 效率设置页面
 * Requirements: 1.5, 9.1
 * - 开启/关闭各项效率功能
 * - 设置工时提醒时间
 * - 设置默认工时数
 * - 自定义快捷键
 */
function EfficiencySettingsPage() {
  const { data: preferences, isLoading, error } = useEfficiencyPreferences()
  const updatePreferences = useUpdateEfficiencyPreferences()
  const toast = useToast()

  // 本地状态用于编辑
  const [editingShortcuts, setEditingShortcuts] = useState(false)
  const [customShortcuts, setCustomShortcuts] = useState<Record<string, string>>({})

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载设置失败: {error.message}</p>
      </div>
    )
  }

  const handleToggle = (key: string, value: boolean) => {
    updatePreferences.mutate(
      { [key]: value },
      {
        onSuccess: () => {
          toast.success('设置已保存', '您的偏好设置已更新')
        },
        onError: (err) => {
          toast.error('保存失败', err.message)
        },
      }
    )
  }

  const handleTimeChange = (time: string) => {
    updatePreferences.mutate(
      { timelogReminderTime: time },
      {
        onSuccess: () => {
          toast.success('设置已保存', '工时提醒时间已更新')
        },
      }
    )
  }

  const handleDefaultHoursChange = (hours: number) => {
    if (hours < 0.5 || hours > 24) {return}
    updatePreferences.mutate(
      { defaultTimelogHours: hours },
      {
        onSuccess: () => {
          toast.success('设置已保存', '默认工时数已更新')
        },
      }
    )
  }

  const handleDailyGoalChange = (hours: number) => {
    if (hours < 1 || hours > 24) {return}
    updatePreferences.mutate(
      { dailyTimelogGoal: hours },
      {
        onSuccess: () => {
          toast.success('设置已保存', '每日工时目标已更新')
        },
      }
    )
  }

  const handleResetToDefaults = () => {
    updatePreferences.mutate(
      {
        enableQuickEntry: true,
        enableSuggestions: true,
        enableShortcuts: true,
        enableDraftAutoSave: true,
        enableNotificationDigest: true,
        timelogReminderTime: '18:00',
        defaultTimelogHours: 2.0,
        dailyTimelogGoal: 8.0,
        doNotDisturb: false,
        customShortcuts: {},
      },
      {
        onSuccess: () => {
          toast.success('已重置', '所有设置已恢复为默认值')
        },
      }
    )
  }

  // 生成时间选项 (每30分钟一个选项)
  const timeOptions = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0')
      const minute = m.toString().padStart(2, '0')
      timeOptions.push(`${hour}:${minute}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">效率设置</h1>
            <p className="text-muted-foreground">配置效率提升功能，优化您的工作体验</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResetToDefaults}
          disabled={updatePreferences.isPending}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          恢复默认
        </Button>
      </div>

      {/* 功能开关区域 */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <h2 className="font-semibold">功能开关</h2>
          <p className="text-sm text-muted-foreground">开启或关闭各项效率提升功能</p>
        </div>
        <div className="divide-y">
          {/* 快速工时记录 */}
          <FeatureToggle
            icon={<Clock className="h-5 w-5" />}
            title="快速工时记录"
            description="在任务列表中直接记录工时，无需打开详情页"
            checked={preferences.enableQuickEntry}
            onCheckedChange={(checked) => handleToggle('enableQuickEntry', checked)}
            disabled={updatePreferences.isPending}
          />

          {/* 智能建议 */}
          <FeatureToggle
            icon={<Lightbulb className="h-5 w-5" />}
            title="智能建议"
            description="根据任务状态自动建议下一步操作"
            checked={preferences.enableSuggestions}
            onCheckedChange={(checked) => handleToggle('enableSuggestions', checked)}
            disabled={updatePreferences.isPending}
          />

          {/* 键盘快捷键 */}
          <FeatureToggle
            icon={<Keyboard className="h-5 w-5" />}
            title="键盘快捷键"
            description="使用快捷键快速导航和操作（按 ? 查看所有快捷键）"
            checked={preferences.enableShortcuts}
            onCheckedChange={(checked) => handleToggle('enableShortcuts', checked)}
            disabled={updatePreferences.isPending}
          />

          {/* 表单自动保存 */}
          <FeatureToggle
            icon={<FileText className="h-5 w-5" />}
            title="表单自动保存"
            description="每 30 秒自动保存表单草稿，防止意外丢失"
            checked={preferences.enableDraftAutoSave}
            onCheckedChange={(checked) => handleToggle('enableDraftAutoSave', checked)}
            disabled={updatePreferences.isPending}
          />

          {/* 通知聚合 */}
          <FeatureToggle
            icon={<Bell className="h-5 w-5" />}
            title="通知聚合"
            description="将同一任务的多条通知合并显示，减少打扰"
            checked={preferences.enableNotificationDigest}
            onCheckedChange={(checked) => handleToggle('enableNotificationDigest', checked)}
            disabled={updatePreferences.isPending}
          />
        </div>
      </div>

      {/* 工时设置区域 */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">工时设置</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            配置工时记录相关的默认值和提醒
          </p>
        </div>
        <div className="p-4 space-y-6">
          {/* 工时提醒时间 */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">工时提醒时间</p>
              <p className="text-xs text-muted-foreground">
                每日在此时间提醒您记录工时（如当日未记录）
              </p>
            </div>
            <Select
              value={preferences.timelogReminderTime ?? '18:00'}
              onValueChange={handleTimeChange}
              disabled={updatePreferences.isPending}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 默认工时数 */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">默认工时数</p>
              <p className="text-xs text-muted-foreground">
                快速记录工时时的默认小时数
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0.5}
                max={24}
                step={0.5}
                value={preferences.defaultTimelogHours ?? 2}
                onChange={(e) => handleDefaultHoursChange(parseFloat(e.target.value))}
                className="w-20 text-center"
                disabled={updatePreferences.isPending}
              />
              <span className="text-sm text-muted-foreground">小时</span>
            </div>
          </div>

          {/* 每日工时目标 */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">每日工时目标</p>
              <p className="text-xs text-muted-foreground">
                设置每日工时目标，用于显示完成进度
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={24}
                step={0.5}
                value={preferences.dailyTimelogGoal ?? 8}
                onChange={(e) => handleDailyGoalChange(parseFloat(e.target.value))}
                className="w-20 text-center"
                disabled={updatePreferences.isPending}
              />
              <span className="text-sm text-muted-foreground">小时</span>
            </div>
          </div>
        </div>
      </div>

      {/* 快捷键自定义区域 */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Keyboard className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold">快捷键设置</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingShortcuts(!editingShortcuts)}
              disabled={!preferences.enableShortcuts}
            >
              {editingShortcuts ? '完成' : '自定义'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            自定义键盘快捷键（按 ? 键查看所有快捷键）
          </p>
        </div>
        <div className="p-4">
          {!preferences.enableShortcuts ? (
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3">
              <Info className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                请先启用键盘快捷键功能
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <ShortcutRow
                label="跳转到我的任务"
                shortcut="G + T"
                customShortcut={customShortcuts['goto_tasks']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, goto_tasks: value })
                }
              />
              <ShortcutRow
                label="跳转到项目列表"
                shortcut="G + P"
                customShortcut={customShortcuts['goto_projects']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, goto_projects: value })
                }
              />
              <ShortcutRow
                label="跳转到仪表板"
                shortcut="G + D"
                customShortcut={customShortcuts['goto_dashboard']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, goto_dashboard: value })
                }
              />
              <ShortcutRow
                label="全局搜索"
                shortcut="Cmd/Ctrl + K"
                customShortcut={customShortcuts['search']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, search: value })
                }
              />
              <ShortcutRow
                label="新建任务"
                shortcut="N"
                customShortcut={customShortcuts['new_task']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, new_task: value })
                }
              />
              <ShortcutRow
                label="记录工时"
                shortcut="L"
                customShortcut={customShortcuts['log_time']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, log_time: value })
                }
              />
              <ShortcutRow
                label="显示快捷键帮助"
                shortcut="?"
                customShortcut={customShortcuts['help']}
                editing={editingShortcuts}
                onCustomize={(value) =>
                  setCustomShortcuts({ ...customShortcuts, help: value })
                }
              />

              {editingShortcuts && (
                <div className="flex items-center gap-2 pt-4 border-t">
                  <Button
                    size="sm"
                    onClick={() => {
                      updatePreferences.mutate(
                        { customShortcuts },
                        {
                          onSuccess: () => {
                            toast.success('快捷键已保存', '自定义快捷键已更新')
                            setEditingShortcuts(false)
                          },
                        }
                      )
                    }}
                    disabled={updatePreferences.isPending}
                  >
                    保存快捷键
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCustomShortcuts({})
                      setEditingShortcuts(false)
                    }}
                  >
                    取消
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 提示信息 */}
      <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-4">
        <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            <strong>提示：</strong>效率功能旨在减少操作步骤，让您专注于核心工作。
          </p>
          <p>
            如果某些功能影响了您的使用习惯，可以随时在此关闭。
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * 功能开关组件
 */
interface FeatureToggleProps {
  icon: React.ReactNode
  title: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
}

function FeatureToggle({
  icon,
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
}: FeatureToggleProps) {
  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-muted-foreground">{icon}</div>
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  )
}

/**
 * 快捷键行组件
 */
interface ShortcutRowProps {
  label: string
  shortcut: string
  customShortcut?: string
  editing: boolean
  onCustomize: (value: string) => void
}

function ShortcutRow({
  label,
  shortcut,
  customShortcut,
  editing,
  onCustomize,
}: ShortcutRowProps) {
  return (
    <div className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50">
      <span className="text-sm">{label}</span>
      {editing ? (
        <Input
          className="w-32 text-center text-xs"
          placeholder={shortcut}
          value={customShortcut ?? ''}
          onChange={(e) => onCustomize(e.target.value)}
        />
      ) : (
        <kbd className="rounded bg-muted px-2 py-1 text-xs font-mono">
          {customShortcut || shortcut}
        </kbd>
      )}
    </div>
  )
}

export default EfficiencySettingsPage
